import React, { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Typography, Button, Box, styled, Dialog as MatDialog, DialogContent, DialogActions } from '@material-ui/core'
import SecurityIcon from '../../../assets/shield-alt.svg'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import STATIC_TEXT from '../../../content/common.json'
import welcomePageStyles from './welcome-page.styles'
import CONSTANTS from '../../../utils/constants'
import Dialog from '../../portico/dialog/dialog'
import FormInput from '../../portico/input/form-input'
import { saveUserEmailAction } from '../../../stores/user/user-action'
import { useDispatch, useSelector } from 'react-redux'
import PublicWrapper from '../../public-wrapper-v2/public-wrapper'
import { userLogin } from '../../../services/identity-service'
import { setSnackBar } from '../../../stores/ui/ui-action'
import CustomLoading from '../../custom/custom-loading'
import { FeatureToggleContext } from '../../../config/toggle.context'
import closeIcon from '../../../assets/close.svg'
import detailedCapTable from '../../../assets/detailed-cap-table.svg'
import boardDeck from '../../../assets/board-deck.svg'
import financialProjections from '../../../assets/financial-projections.svg'
import valuation from '../../../assets/409a-valuation.svg'
import netsuiteIcon from '../../../assets/icon-netsuite.svg'
import xeroIcon from '../../../assets/icon-xero.svg'
import quickBooksLogo from '../../../assets/logo-quickbooks.svg'
import waveLogo from '../../../assets/logo-wave.svg'
import { isRestrictedEmail } from '../../../utils/domain-utils'

const EmailDialog = styled(MatDialog)(({ theme }) => ({
    '& .MuiDialog-paperWidthSm': {
        maxWidth: '536px'
    },
    '& .MuiDialogContent-root': {
        padding: '8px 8px 0px 8px'
    }
}))

const WelcomePage = () => {
    const user = useSelector((state: any) => state.user)
    const { EMAIL_REGEX } = CONSTANTS
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [emailNotFoundError, setEmainNotFoundError] = useState<boolean>(false)
    const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false)
    const { useToggle } = useContext(FeatureToggleContext)
    const toggleSendEmailToNewUser = useToggle('ftSendEmailToNewUser')
    const sendEmailToNewUser = toggleSendEmailToNewUser(false, true)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const classes = welcomePageStyles()
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setError
    } = useForm({
        defaultValues: { email: user.email }
    })

    const onOkDialogClose = () => {
        setShowEmailDialog(false)
    }
    const [isBlocked, setIsBlocked] = useState(false)

    const onSubmit = async (data) => {
        if (!data.email.match(EMAIL_REGEX)) {
            setError('email', { message: 'Please enter a valid email' })
            return
        }
        if (isRestrictedEmail(data.email)) {
            setError('email', {
                message: 'You do not have access to this application. Contact lendxappsupport@firstcitizens.com.'
            })
            setIsBlocked(true)
            return
        }
        setIsBlocked(false)
        setSubmitting(true)
        try {
            const loginResponse = await userLogin(data.email, 'welcome')
            dispatch(saveUserEmailAction(data.email))
            setSubmitting(false)
            setEmainNotFoundError(false)
            if (sendEmailToNewUser || !loginResponse.data.error) {
                setShowEmailDialog(true)
            } else {
                setEmainNotFoundError(true)
                navigate('/contact-info')
            }
        } catch (e) {
            setSubmitting(false)
            setEmainNotFoundError(false)
            dispatch(
                setSnackBar({
                    show: true,
                    message: 'Login Failed',
                    messageType: 'error'
                })
            )
        }
    }

    const AccountingSoftwareToolTip = styled(({ className, ...props }: TooltipProps) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.arrow}`]: {
            color: '#007DB8',
            position: 'absolute',
            width: 'height'
        },
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: '#007DB8',
            padding: '16px',
            width: 'hug',
            height: 'hug'
        }
    }))

    return (
        <>
            {submitting && <CustomLoading message="Logging in..." />}
            <div className={classes.trapezoidBg}>
                <PublicWrapper
                    data-testid="publicWrapper"
                    contentWrapperClass={classes.welcomePageWrapper}
                    content={
                        <div className={classes.flexBox}>
                            <div className={classes.leftBox}>
                                <Typography style={{ fontWeight: '700' }} className={classes.bannerTitle}>
                                    Let's Talk Lending!
                                </Typography>

                                <Typography className={classes.secondaryContent}>This should take about 20-30 minutes.</Typography>

                                <Typography className={classes.whatToExpect}>Be prepared to:</Typography>

                                <div className={classes.bePreparedToContents}>
                                    <Box display={'flex'} paddingBottom={'16px'}>
                                        <div className={classes.stepCount}>1</div>
                                        <div className={classes.contentHeader}>Upload basic due diligence documents (if applicable):</div>
                                    </Box>
                                    <Box paddingBottom={'32px'} paddingLeft={'40px'}>
                                        <div className={classes.documentsRowOne}>
                                            <div className={classes.documentColumn}>
                                                <img className={classes.documentImage} src={detailedCapTable} />
                                                Detailed Cap Table
                                            </div>
                                            <div className={classes.documentColumn}>
                                                <img className={classes.documentImage} src={boardDeck} />
                                                Board Deck
                                            </div>
                                        </div>
                                        <div className={classes.documentsRowTwo}>
                                            <div className={classes.documentColumn}>
                                                <img className={classes.documentImage} src={financialProjections} />
                                                Financial Projections
                                            </div>
                                            <div className={classes.documentColumn}>
                                                <img className={classes.documentImage} src={valuation} />
                                                409A Valuation
                                            </div>
                                        </div>
                                    </Box>
                                    <Box display={'flex'}>
                                        <div className={classes.stepCount}>2</div>
                                        <div className={classes.contentHeader}>Provide historical financials</div>
                                    </Box>
                                    <Box paddingBottom={'32px'} paddingLeft={'40px'}>
                                        <Box paddingBottom={'16px'} paddingTop={'8px'}>
                                            You’ll either be asked to connect your&nbsp;
                                            <AccountingSoftwareToolTip
                                                placement={'bottom-start'}
                                                title={
                                                    <Typography
                                                        style={{
                                                            fontWeight: 400,
                                                            fontSize: '14px',
                                                            lineHeight: '20px',
                                                            fontFamily: 'Roboto',
                                                            color: 'var(--white)'
                                                        }}
                                                    >
                                                        Should you accept a loan from First Citizens Bank, connecting your accounting
                                                        software prevents you from needing to manually upload accounting documents each
                                                        month.
                                                    </Typography>
                                                }
                                                arrow
                                            >
                                                <span>
                                                    <a className={classes.tooltipText}>accounting software</a>
                                                </span>
                                            </AccountingSoftwareToolTip>
                                            &nbsp;or upload your Balance Sheet and Income Statement manually.
                                        </Box>
                                        <Box paddingBottom={'16px'}>Accounting providers we support:</Box>
                                        <div className={classes.providersLogoContainer}>
                                            <img src={quickBooksLogo} />
                                            <img src={xeroIcon} />
                                            <img src={waveLogo} />
                                            <img src={netsuiteIcon} />
                                        </div>
                                    </Box>
                                    <Box display={'flex'}>
                                        <div className={classes.stepCount}>3</div>
                                        <div className={classes.contentHeader}>
                                            Share the top milestones you’re tracking with your investors
                                        </div>
                                    </Box>
                                </div>
                            </div>
                            <div className={classes.rightBox}>
                                <Box
                                    width={474}
                                    minHeight={326}
                                    bgcolor={'#FFFFFF'}
                                    boxShadow={2}
                                    borderRadius={'4px'}
                                    padding={'27px 24px'}
                                    className={classes.rightInnerBox}
                                >
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <div>
                                            <Typography
                                                style={{
                                                    fontSize: '24px',
                                                    fontWeight: '500',
                                                    overflowWrap: 'break-word',
                                                    marginBottom: '24px',
                                                    lineHeight: '34px'
                                                }}
                                            >
                                                <span className={classes.applicationText}>
                                                    {' '}
                                                    Enter your email to start or reaccess your application{' '}
                                                </span>
                                            </Typography>
                                            <div>
                                                <FormInput
                                                    data-testid="email"
                                                    name="email"
                                                    label={'Work Email'}
                                                    isRequired={true}
                                                    ref={register('email')}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                        setIsBlocked(false)
                                                        if (!e.target.value.match(EMAIL_REGEX)) {
                                                            setError('email', { message: 'Please enter a valid email' })
                                                        }
                                                    }}
                                                    debouncetime={500}
                                                    error={errors['email'] as any}
                                                    setValue={setValue}
                                                />
                                            </div>
                                            <Button
                                                className={classes.continueBtn}
                                                data-testid="continue-btn"
                                                type="submit"
                                                disabled={isBlocked || submitting}
                                            >
                                                {'Continue'}
                                            </Button>

                                            <div
                                                style={{
                                                    display: 'flex',
                                                    marginTop: '24px'
                                                }}
                                            >
                                                <div>
                                                    <Typography
                                                        style={{
                                                            paddingRight: '8.5px'
                                                        }}
                                                    >
                                                        <span>
                                                            <img src={SecurityIcon} alt="Shield " />
                                                        </span>
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Typography className={classes.securityInfoTitle}>
                                                        <span>{STATIC_TEXT.login.securityInfoTitle}</span>
                                                    </Typography>
                                                    <Typography className={classes.securityInfoContent}>
                                                        <span>{STATIC_TEXT.login.securityInfoContent}</span>
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </Box>
                            </div>
                        </div>
                    }
                >
                    {sendEmailToNewUser ? (
                        <EmailDialog open={showEmailDialog} data-testid="email-dialog">
                            <DialogContent>
                                <div className={classes.emailDialogMainContainer}>
                                    <a className={classes.closeIcon} data-testid="close-dialog-icon" onClick={onOkDialogClose}>
                                        <img src={closeIcon} />
                                    </a>
                                    <div className={classes.emailDialogContentContainer}>
                                        <p className={classes.emailDialoglHeading}>{STATIC_TEXT.login.CheckYourEmailTitle}</p>
                                        <div className={classes.emailDialogContents}>
                                            <div>
                                                We’ve sent you an email to either start or resume an application. It should arrive within a
                                                few minutes.
                                            </div>
                                            <div>
                                                If you have any issues, please contact{' '}
                                                <a
                                                    className={classes.lendexSupportLink}
                                                    href="mailto:lendxappsupport@firstcitizens.com"
                                                    target="_blank"
                                                >
                                                    LendX Support
                                                </a>
                                                .
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button className={classes.closeButton} data-testid="close-dialog-btn" onClick={onOkDialogClose}>
                                    Close
                                </Button>
                            </DialogActions>
                        </EmailDialog>
                    ) : (
                        <Dialog
                            title={STATIC_TEXT.login.CheckYourEmailTitle}
                            open={showEmailDialog}
                            okBtnLabel={CONSTANTS.OKAY}
                            onOk={onOkDialogClose}
                            okBtnClasses={classes.btn_primary}
                            disableActions={false}
                            closeOnEscOrClick={false}
                            customClass={classes.dialogTitle}
                            {...({
                                children: (
                                    <>
                                        {STATIC_TEXT.login.CheckYourEmailMessage}
                                        <br />
                                        {STATIC_TEXT.login.PleaseCheckYourEmail}
                                    </>
                                )
                            } as any)}
                        />
                    )}
                </PublicWrapper>
            </div>
        </>
    )
}

export default WelcomePage
